import React, { useState, useEffect, useCallback } from 'react';
import { whatsappAPI } from '../api';
import { RefreshCw, CheckCircle2, Wifi, WifiOff, Smartphone, Info } from 'lucide-react';

export default function WhatsAppPanel({ sessionId }) {
  const [qrData, setQrData] = useState(null);
  const [status, setStatus] = useState('initializing');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const fetchQR = useCallback(async () => {
    try {
      const res = await whatsappAPI.getQR(sessionId);
      const { status: s, qr } = res.data;
      setStatus(s);
      setQrData(qr || null);
      setConnected(s === 'connected');
    } catch (err) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Initialize session on mount
  useEffect(() => {
    whatsappAPI.init(sessionId).catch(() => {});
  }, [sessionId]);

  // Poll for QR/status
  useEffect(() => {
    fetchQR();
    const interval = setInterval(() => {
      if (!connected) {
        fetchQR();
        setPollCount((c) => c + 1);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [fetchQR, connected]);

  const statusConfig = {
    connected: { label: 'Connected', color: 'bg-green-100 text-green-700 border-green-300', icon: <CheckCircle2 size={14} /> },
    authenticated: { label: 'Authenticated', color: 'bg-green-100 text-green-700 border-green-300', icon: <CheckCircle2 size={14} /> },
    qr_ready: { label: 'Scan QR Code', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: <Smartphone size={14} /> },
    initializing: { label: 'Initializing...', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: <RefreshCw size={14} className="animate-spin" /> },
    disconnected: { label: 'Disconnected', color: 'bg-red-100 text-red-700 border-red-300', icon: <WifiOff size={14} /> },
    error: { label: 'Error', color: 'bg-red-100 text-red-700 border-red-300', icon: <WifiOff size={14} /> },
  };

  const cfg = statusConfig[status] || statusConfig['initializing'];

  const instructions = [
    { num: 1, text: 'Enter your school name in the header above' },
    { num: 2, text: 'Scan the WhatsApp QR code to connect your number' },
    { num: 3, text: 'Tab 1: Single subject per row Excel upload' },
    { num: 4, text: 'Tab 2: All subjects as columns — auto-computes % and Percentile Rank' },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* QR Card */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span className="text-white font-semibold text-sm">WhatsApp Connection</span>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center gap-1 ${cfg.color}`}>
            {cfg.icon}
            {cfg.label}
          </span>
        </div>

        {/* QR / Connected State */}
        <div className="p-4 flex flex-col items-center">
          {connected ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 size={36} className="text-green-500" />
              </div>
              <div className="text-center">
                <p className="font-bold text-green-600 text-base">WhatsApp Connected!</p>
                <p className="text-xs text-gray-500 mt-1">You can now send messages to students</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Wifi size={12} className="text-green-500" />
                <span>Session: {sessionId}</span>
              </div>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Starting WhatsApp client...</p>
            </div>
          ) : qrData ? (
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-white border-4 border-green-400 rounded-xl shadow-inner">
                <img
                  src={qrData}
                  alt="WhatsApp QR Code"
                  className="w-44 h-44 object-contain"
                />
              </div>
              <p className="text-xs text-center text-gray-500 leading-relaxed px-2">
                Open WhatsApp → Linked Devices → Link a Device → Scan this code
              </p>
              <button
                onClick={fetchQR}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                <RefreshCw size={12} />
                Refresh QR
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500 text-center">
                Generating QR code...
                <br />
                <span className="text-xs text-gray-400">({pollCount} polls)</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* How to Use */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center gap-2">
          <Info size={16} className="text-white" />
          <span className="text-white font-semibold text-sm">How to Use</span>
        </div>
        <div className="p-4">
          <ol className="space-y-3">
            {instructions.map((item) => (
              <li key={item.num} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  {item.num}
                </span>
                <span className="text-sm text-gray-600 leading-snug">{item.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
